import { z } from 'zod';

const string = () => z.string();

const number = () => z.coerce.number();

const boolean = () => z.boolean();

const email = (
  invalidMsg: string,
  requiredMsg: string,
) => z.string().min(1, requiredMsg).email(invalidMsg);

// Mirrors yup's `.email()` default (`excludeEmptyString: true`): an empty
// value is valid, but any non-empty value must be a well-formed email.
const optionalEmail = (invalidMsg: string) => z.string().refine(
  (value) => value === '' || z.string().email().safeParse(value).success,
  invalidMsg,
);

// Preserves Yup's formatted-phone cleanup before enforcing the configured
// normalized length.
const phoneNumber = (
  invalidMsg: string,
  requiredMsg: string,
  min = 11,
) => z.preprocess(
  (value) => `${value ?? ''}`.replace(/[_{()}{+}-\s]/g, ''),
  z.string().min(1, requiredMsg).min(min, invalidMsg),
);

// RHF address fields submit text values, so normalize numeric text before
// requiring a positive ZIP value.
const zipCode = (invalidMsg: string, requiredMsg = invalidMsg) => z.preprocess(
  (value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    return typeof value === 'string' ? Number(value) : value;
  },
  z.number({ required_error: requiredMsg, invalid_type_error: invalidMsg }).finite(invalidMsg).gt(0, invalidMsg),
);

// yup's `.required()` treats `null` the same as `undefined` (both fail with
// the required message). Zod's `required_error` only fires for `undefined`;
// for `null` it falls through to the type check, which for requiredNumber
// would otherwise silently coerce `null` -> 0 and pass. Normalize `null` to
// `undefined` first so all three behave like yup here.
const nullToUndefined = (value: unknown) => (value === null ? undefined : value);

export default {
  string,
  requiredString: (requiredMsg: string) => z.preprocess(
    nullToUndefined,
    z.string({ required_error: requiredMsg, invalid_type_error: requiredMsg }).min(1, requiredMsg),
  ),
  number,
  requiredNumber: (requiredMsg: string) => z.preprocess(
    nullToUndefined,
    z.coerce.number({ required_error: requiredMsg, invalid_type_error: requiredMsg }),
  ),
  boolean,
  requiredBoolean: (requiredMsg: string) => z.preprocess(
    nullToUndefined,
    z.boolean({ required_error: requiredMsg, invalid_type_error: requiredMsg }),
  ),
  email,
  optionalEmail,
  phoneNumber,
  zipCode,
};
